<script lang="ts">
export default {
    name: 'BasePopup',
};
</script>
<script lang="ts" setup>
import Box from '@pet/base.box/box.vue';
import SFTransition from '@pet/base.transition/index.vue';
import type { SFTransitionName as AnimationType } from '@pet/base.transition/types';
import { useBarInfo, isAtFourTab, isInImmersiveTab, isInBrowser } from '@pet/base.utils';
import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue-demi';

import CloseBtn from './components/close.vue';
import PopupMask from './components/popup-mask.vue';
import { LOCK_SCREEN_CLASS } from './const';
import Pilot from './pilot.vue';
import type { ClosePosType, PositionType, PilotType } from './types';

interface PopupProps {
    /**
     * 显示隐藏
     */
    show?: boolean;
    /**
     * 动画类型
     * "no-effect" | "fade" | "drawer-top" | "drawer-left" | "drawer-right" | "drawer-bottom" | "scale-in-out" | "pop" | "flip-pop" | "scale-out" | "flip"
     */
    aniType?: AnimationType;
    /**
     * 暂缓显示时长
     */
    enterDelay?: number;
    /**
     * 进入时间持续时长
     */
    enterDuration?: number;
    /**
     * 非主要内容进入延时
     */
    addonEnterDelay?: number;
    /**
     * 离开延迟
     */
    leaveDelay?: number;
    /**
     * 离开耗时
     */
    leaveDuration?: number;
    /**
     * 自带关闭不可用
     */
    disableClose?: boolean;
    /**
     * 关闭位置
     * "top" | "bottom"
     */
    closePos?: ClosePosType;
    /**
     * 展示关闭
     */
    showClose?: boolean;
    /**
     * 模态主内容展示位置
     * "top" | "center" | "bottom" | "left" | "right"
     */
    position?: PositionType;
    /**
     * 遮罩点击关闭
     */
    maskCloseable?: boolean;
    /**
     * 是否为透明遮罩
     */
    transparent?: boolean;
    /**
     * 是否展示遮罩
     */
    mask?: boolean;
    /**
     * 遮罩主内容是否支持滚动
     */
    innerScroll?: boolean;
    /**
     * 主内容关闭后飞向的dom id
     */
    flyToTarget?: string;
    /**
     * 是否自动执行飞入动作
     */
    autoFly?: boolean;
    /**
     * 最小飞离时长
     */
    minFlyDuration?: number;
    /**
     * 4tab适配兼容
     */
    needTabSpace?: boolean;
}

const props = withDefaults(defineProps<PopupProps>(), {
    show: false,
    enterDelay: 0,
    enterDuration: 300,
    addonEnterDelay: 0,
    leaveDelay: 0,
    leaveDuration: 233,
    disableClose: false,
    closePos: 'top',
    position: 'center',
    showClose: true,
    maskCloseable: true,
    transparent: false,
    mask: true,
    innerScroll: false,
    flyToTarget: '',
    autoFly: true,
    minFlyDuration: 667,
    needTabSpace: true,
});

const emit = defineEmits<{
    /**
     * 可见状态
     * @arg { boolean } show
     */
    (event: 'update:show', show: boolean): void;
    /**
     * 关闭按钮点击
     */
    (event: 'close'): void;
    /**
     * 遮罩关闭
     */
    (event: 'mask-close'): void;
    /**
     * 进入前
     */
    (event: 'before-enter'): void;
    /**
     * 进入
     */
    (event: 'enter'): void;
    /**
     * 进入后
     */
    (event: 'after-enter'): void;
    /**
     * 离开前
     */
    (event: 'before-leave'): void;
    /**
     * 离开
     */
    (event: 'leave'): void;
    /**
     * 离开后
     */
    (event: 'after-leave'): void;
}>();

const isInit = ref(false);
let scrollTop = 0;
const root = isInBrowser ? document.documentElement : null;
const app = isInBrowser ? document.body : null;
const orgTop = app ? getComputedStyle(app).getPropertyValue('top') : 'auto';
const barInfo = useBarInfo();
const fixSpace = computed(() => {
    return {
        bottom: Boolean(barInfo.toolBarHeight) ? `${barInfo.bottomHeight}px` : 0,
        paddingTop: isAtFourTab() && !isInImmersiveTab() && props.needTabSpace ? `${barInfo.statusBarHeight}px` : 0,
    };
});

const perspective = computed(() => props.aniType?.includes('flip') === true && 'popup-perspective');

const noMask = computed(() => !props.mask && 'no-mask');

function setLock() {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    scrollTop = window.scrollY || (root?.scrollTop ?? 0);
    if (root != null) {
        root.classList.add(LOCK_SCREEN_CLASS);
    }
    if (app != null) {
        app.style.top = `-${scrollTop}px`;
    }
}
function removeLock() {
    if (root != null) {
        root.classList.remove(LOCK_SCREEN_CLASS);
        window.scrollTo(0, scrollTop);
    }

    if (app != null) {
        if (orgTop === 'auto') {
            app.style.removeProperty('top');
        } else {
            app.style.top = orgTop;
        }
    }
}

// eslint-disable-next-line vue/no-setup-props-destructure
const showRef = ref(props.show);
// eslint-disable-next-line vue/no-setup-props-destructure
const wrapperShowRef = ref(props.show);
watch(
    () => props.show,
    (val) => {
        showRef.value = val;
        nextTick(() => {
            wrapperShowRef.value = val;
        });
        if (props.innerScroll && val) {
            setLock();
        }
    },
    {
        immediate: true,
    },
);

const addonsClasses = computed(() => (props.closePos === 'top' ? 'addons-top' : 'addons-bottom'));
const contentStay = computed(() => `popup-content-${props.position ?? 'center'}`);
const changeLeaveDuration = ref(false);
const needLeaveFly = computed(() => changeLeaveDuration.value || (Boolean(props.flyToTarget) && props.autoFly));
const leaveDurationSet = computed(() => {
    const flyAtLeastTime = Math.max(props.minFlyDuration, props.leaveDuration);
    return needLeaveFly.value ? flyAtLeastTime : props.leaveDuration;
});
const transDuration = computed(() => {
    return {
        enter: +props.enterDuration + props.enterDelay,
        leave: +leaveDurationSet.value + props.leaveDelay,
    };
});

const aniTypeComputed = computed<AnimationType>(() => {
    // flyout use fade leave animation
    if (isInit.value && needLeaveFly.value) {
        return 'no-effect';
    }
    if (typeof props.aniType === 'string') {
        return props.aniType;
    } else if (props.position !== 'center') {
        return `drawer-${props.position}`;
    }
    return 'pop';
});

const transDelay = computed(() => {
    return {
        enter: props.enterDelay,
        leave: props.leaveDelay,
    };
});

const addonsTransDelay = computed(() => {
    return {
        enter: props.addonEnterDelay,
        leave: props.leaveDelay,
    };
});

const addonsTransDuration = computed(() => {
    return {
        enter: 167,
        leave: needLeaveFly.value ? 233 : props.leaveDuration,
    };
});

const maskTransDuration = computed(() => {
    return {
        enter: props.enterDuration < 233 ? props.enterDuration : 233,
        leave: needLeaveFly.value ? 433 : 233,
    };
});

const maskTransDelay = {
    leave: 67,
};

const closeColor = computed(() => (!props.mask || props.transparent ? '#000' : '#fff'));

function touchmoveHandle(e: Event) {
    if (!props.innerScroll) {
        e.preventDefault();
    }
}

function triggerLeave() {
    showRef.value = false;
}

function handleMainContentLeave() {
    nextTick(() => {
        emit('update:show', false);
    });
}

function handleCloseBtnClick() {
    changeLeaveDuration.value = false;
    triggerLeave();
    emit('close');
}

function maskClick() {
    if (props.maskCloseable) {
        triggerLeave();
        emit('mask-close');
    }
}

const pilotRef = ref<PilotType | null>(null);

function bootToTarget() {
    changeLeaveDuration.value = true;
    nextTick(() => {
        pilotRef.value?.boot();
        emit('update:show', false);
    });
}

watch(showRef, (val) => {
    if (!val && props.autoFly && Boolean(props.flyToTarget)) {
        pilotRef.value?.boot();
    }
});

function initState() {
    isInit.value = false;
    changeLeaveDuration.value = false;
}

function beforeEnter() {
    emit('before-enter');
}

function enter() {
    isInit.value = true;
    emit('enter');
}

function afterEnter() {
    emit('after-enter');
}

function beforeLeave() {
    emit('before-leave');
}

function leave() {
    emit('leave');
    initState();
    if (props.innerScroll) {
        removeLock();
    }
}

function afterLeave() {
    emit('after-leave');
}

defineExpose({
    /**
     * 执行飞入动画
     */
    boot: bootToTarget,
    /**
     * 关闭当前模态
     */
    close: triggerLeave,
});

onBeforeUnmount(() => {
    if (props.innerScroll && isInit.value) {
        removeLock();
    }
    initState();
});
</script>

<template>
    <SFTransition
        appear
        :duration="transDuration"
        name="no-effect"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @leave="leave"
        @before-leave="beforeLeave"
        @after-leave="afterLeave"
    >
        <Box
            v-if="wrapperShowRef"
            justify="center"
            align="center"
            direction="col"
            class="popup"
            :class="[perspective, noMask]"
            :style="fixSpace"
        >
            <SFTransition appear :duration="maskTransDuration" :delay="maskTransDelay">
                <PopupMask v-if="mask && showRef" :transparent="transparent" class="popup-mask" @click="maskClick" />
            </SFTransition>
            <div class="popup-main" :class="contentStay" @touchmove="touchmoveHandle">
                <SFTransition appear :delay="addonsTransDelay" :duration="addonsTransDuration">
                    <Box v-if="showRef" direction="col" justify="center" class="popup-addons" :class="addonsClasses">
                        <!-- @slot 关闭按钮平行位置 可加logo等 -->
                        <slot name="addons" />
                        <!-- 不展示，但用来占位 -->
                        <div v-if="$slots['content-header']" class="popup-header-placeholder">
                            <slot name="content-header" />
                        </div>
                        <CloseBtn
                            v-if="showClose"
                            class="popup-close"
                            :fill="closeColor"
                            @click="handleCloseBtnClick"
                        />
                    </Box>
                </SFTransition>
                <SFTransition appear :delay="transDelay" :duration="addonsTransDuration">
                    <header v-if="$slots.header && showRef" class="popup-header">
                        <!-- @slot 顶部区域 -->
                        <slot name="header" />
                    </header>
                </SFTransition>
                <SFTransition
                    appear
                    :name="aniTypeComputed"
                    :duration="transDuration"
                    :delay="transDelay"
                    @after-leave="handleMainContentLeave"
                >
                    <div v-if="showRef || changeLeaveDuration">
                        <Pilot
                            v-if="flyToTarget"
                            ref="pilotRef"
                            class="popup-content"
                            :duration="leaveDurationSet"
                            :fly-to-target="flyToTarget"
                        >
                            <div v-if="$slots['content-header']" class="popup-content-header">
                                <slot name="content-header" />
                            </div>
                            <template v-if="$slots.back">
                                <div class="popup-content-back">
                                    <!-- @slot 翻转效果背部 -->
                                    <slot name="back" />
                                </div>
                                <div class="popup-content-front">
                                    <!-- @slot 弹层主内容 -->
                                    <slot />
                                </div>
                            </template>
                            <!-- @slot 弹层主内容 -->
                            <slot v-else />
                        </Pilot>
                        <template v-else>
                            <div v-if="$slots['content-header']" class="popup-content-header">
                                <slot name="content-header" />
                            </div>
                            <template v-if="$slots.back">
                                <div class="popup-content-back">
                                    <slot name="back" />
                                </div>
                                <div class="popup-content-front">
                                    <slot />
                                </div>
                            </template>
                            <slot v-else />
                        </template>
                    </div>
                </SFTransition>
                <SFTransition appear :delay="addonsTransDelay" :duration="addonsTransDuration">
                    <Box v-if="$slots.footer && showRef" justify="center" tag="footer" class="popup-footer">
                        <!-- @slot 弹层底部 -->
                        <slot name="footer" />
                    </Box>
                </SFTransition>
            </div>
            <div v-if="$slots.extension" class="popup-extension">
                <!-- @slot 模态同级容器 -->
                <slot name="extension" />
            </div>
        </Box>
    </SFTransition>
</template>

<style lang="scss" scoped>
@use 'sass:map';

$close-btn-top: (
    x: 20px,
    y: -20px,
);

$close-btn-bottom: (
    x: 50%,
    y: -20px,
);

.popup {
    position: fixed;
    z-index: 9999;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    &.no-mask {
        pointer-events: none;
    }
}

.popup-perspective {
    .popup-mask {
        transform: translateZ(-200px) scale(1.2);
    }
    .popup-plugin {
        transform: translateZ(-200px) scale(1.2);
    }
    .popup-main {
        perspective: 1000px;
    }

    .popup-content,
    :deep(.pilot-container) {
        transform-style: preserve-3d;
    }
}

.popup-main,
.popup-content,
.popup-header {
    position: relative;
    z-index: 1;
    pointer-events: auto;
}

.popup-main {
    max-width: 414px;
}

.popup-content {
    &-top {
        margin-bottom: auto;
    }

    &-bottom {
        margin-top: auto;
    }

    &-left {
        align-self: flex-start;
    }

    &-right {
        align-self: flex-end;
    }
}

.popup-header-placeholder {
    visibility: hidden;
}

.popup-content-header {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    transform: translateY(-100%);
}

.popup-content-back {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    backface-visibility: hidden;
    transform: rotateY(180deg);
}

.popup-content-front {
    backface-visibility: hidden;
}

.popup-addons {
    position: absolute;
    width: 100vw;
    max-width: 414px;
    &.addons-top {
        top: 0;
        left: 50%;
        transform: translate(-50%, -100%);
        .popup-close {
            position: absolute;
            transform: translate(0, -100%);
            top: map.get($close-btn-top, 'y');
            right: map.get($close-btn-top, 'x');
        }
    }

    &.addons-bottom {
        bottom: 0;
        transform: translate(0, -100%);
        width: 100%;
        .popup-close {
            position: absolute;
            bottom: map.get($close-btn-bottom, 'y');
            right: map.get($close-btn-bottom, 'x');
            transform: translate(50%, 100%);
        }
    }
}

.popup-footer {
    position: absolute;
    width: 100%;
    left: 0;
    bottom: 0;
    transform: translate(0, 100%);
}
.popup-extension {
    position: fixed;
    top: 0;
    bottom: 0;
    width: 100%;
    max-width: 414px;
    margin: auto;
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
.u-lock-screen {
    width: 100%;
    height: 100%;
    body {
        position: fixed;
        overflow: hidden;
        width: 414px;
    }
}
</style>
